import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useApp } from '@/context/AppContext';
import { CheckCircle, Heart, Users, Award, Info, ChevronRight, Share2 } from 'lucide-react';
import ProductInfoModal from '@/components/ProductInfoModal';
import { useQuote } from '@/hooks/useQuote';
import { convertKitToBundleInfo } from '@/services/quoteService';

// Mock plan data - this could be expanded to include different plan options
const plans = [
  {
    id: 'buno-plus',
    name: 'Programa de Acompañamiento',
    brand: 'buno',
    price: 2500,
    features: [
      { icon: Users, text: 'Acompañamiento 24/7 con especialistas' },
      { icon: Award, text: '50% mayor adherencia al tratamiento' },
      { icon: CheckCircle, text: 'Soporte técnico y ajustes personalizados' },
      { icon: Heart, text: 'Seguimiento médico continuo' }
    ],
    description: 'El programa buno+ incluye sesiones de seguimiento, ajustes remotos, y soporte 24/7 para maximizar los beneficios de tu tratamiento CPAP.',
    image: 'https://buno1storage1account.blob.core.windows.net/www-buno/buno-tu-companero-cpap-35/bunoplus_2.png',
    recommended: true
  }
];

// Retention offer shown when user tries to remove buno+ plan
const retentionOffer = {
  id: 'buno-plus-retention',
  name: 'Programa de Acompañamiento buno+',
  brand: 'buno',
  price: 2500,
  features: [
    { icon: Users, text: 'Acompañamiento 24/7 con especialistas' },
    { icon: Award, text: '50% mayor adherencia al tratamiento' },
    { icon: CheckCircle, text: 'Ajustes personalizados de tu equipo' },
    { icon: Heart, text: 'Seguimiento médico continuo' },
    { icon: CheckCircle, text: 'Maximización de beneficios del tratamiento' }
  ],
  description: 'Las personas que usan CPAP con un programa como el nuestro tienen una adherencia 50% mayor y mejores resultados en su tratamiento.',
  image: 'https://buno1storage1account.blob.core.windows.net/www-buno/buno-tu-companero-cpap-35/bunoplus_2.png',
  recommended: true,
  type: 'retention'
};

export default function PlanSeleccionStep() {
  const navigate = useNavigate();
  const { state, updateEleccionProgress } = useApp();
  const { createQuote, updateStage, shareQuoteUrl, currentQuote, isLoading: quoteLoading, error: quoteError } = useQuote();
  
  const selectedKit = state.eleccion.kitSeleccionado;

  // Handle header back button clicks
  useEffect(() => {
    const handleHeaderBack = () => {
      navigate('/eleccion/productos');
    };

    window.addEventListener('headerBackClick', handleHeaderBack);
    return () => window.removeEventListener('headerBackClick', handleHeaderBack);
  }, [navigate]);

  const { bunoIncluido } = state.eleccion;

  const [showProductModal, setShowProductModal] = useState<boolean>(false);
  const [selectedPlanForInfo, setSelectedPlanForInfo] = useState<typeof plans[0] | typeof retentionOffer | null>(null);

  const handlePlanToggle = (planId: string) => {
    if (planId === 'buno-plus') {
      if (bunoIncluido) {
        setSelectedPlanForInfo(retentionOffer);
        setShowProductModal(true);
      } else {
        updateEleccionProgress({ bunoIncluido: true });
      }
    }
  };

  const confirmRemovePlan = () => {
    updateEleccionProgress({ bunoIncluido: false });
    setShowProductModal(false);
    setSelectedPlanForInfo(null);
  };

  const keepPlan = () => {
    updateEleccionProgress({ bunoIncluido: true });
    setShowProductModal(false);
    setSelectedPlanForInfo(null);
  };

  const openPlanInfo = (plan: typeof plans[0]) => {
    setSelectedPlanForInfo(plan);
    setShowProductModal(true);
  };

  const closePlanModal = () => {
    setShowProductModal(false);
    setSelectedPlanForInfo(null);
  };

  const continuar = async () => {
    if (!selectedKit) {
      console.error('No kit selected');
      navigate('/eleccion/productos');
      return;
    }

    try {
      // Convert selected kit to BundleInfo for quote creation
      const bundleInfo = convertKitToBundleInfo(
        selectedKit,
        state.eleccion.maskSizing?.maskSize
      );
      
      // Create quote with current kit and plan selection
      const quote = await createQuote({
        stage: 'plan-seleccion',
        bundleInfo
      });
      
      if (quote) {
        // Redirect to cotización URL with human-readable ID
        window.location.href = quote.url;
      } else {
        // Fallback: navigate to regular cart
        console.warn('Failed to create quote, navigating to regular cart');
        navigate('/eleccion/carrito');
      }
    } catch (error) {
      console.error('Failed to create quote:', error);
      // Fallback: navigate to regular cart
      navigate('/eleccion/carrito');
    }
  };

  const handleShareQuote = async () => {
    try {
      if (currentQuote) {
        await updateStage('plan-seleccion');
        await shareQuoteUrl();
      }
    } catch (error) {
      console.error('Failed to share quote:', error);
    }
  };

  return (
    <div className="flex flex-col min-h-[calc(100vh-80px)] overflow-x-hidden">
      <div className="flex-1 container px-4 py-6 sm:py-8 pb-24 max-w-none">
        <div className="max-w-4xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-primary mb-2">
              Confirma tu plan de acompañamiento
            </h1>
            <p className="text-lg text-muted-foreground">
              Maximiza los beneficios de tu tratamiento CPAP
            </p>
          </div>

          {/* Plans Section */}
          <div className="mb-8">
            <div className="grid gap-6">
              {plans.map(plan => (
                <Card 
                  key={plan.id}
                  className={`cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.01] border-2 overflow-hidden ${
                    bunoIncluido 
                      ? 'border-primary shadow-md ring-2 ring-primary/20' 
                      : 'border-border hover:border-primary/50'
                  }`}
                  style={{ boxSizing: 'border-box' }}
                  onClick={() => handlePlanToggle(plan.id)}
                >
                  <CardContent className="p-6 relative">                    
                    {/* Selection indicator */}
                    <div className="absolute top-4 right-4">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                        bunoIncluido 
                          ? 'bg-primary border-primary' 
                          : 'border-muted-foreground'
                      }`}>
                        {bunoIncluido && (
                          <CheckCircle className="h-4 w-4 text-primary-foreground" />
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 bg-accent/10 rounded-lg overflow-hidden border border-accent/20 flex items-center justify-center">
                        <img 
                          src={plan.image}
                          alt={`${plan.name}`} 
                          className="w-full h-full object-contain" 
                        />
                      </div>
                      <div className="flex-1 min-w-0 pr-10">
                        <div>
                          <h3 className="text-lg sm:text-xl font-bold text-primary mb-1">{plan.name}</h3>
                          <p className="text-sm text-muted-foreground">{plan.brand}</p>
                          <p className="text-xl sm:text-2xl font-bold text-accent mt-2">
                            ${plan.price.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>

                    <ul className="text-sm text-muted-foreground space-y-3 mb-6">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-center gap-3">
                          <feature.icon className="h-4 w-4 text-accent flex-shrink-0" />
                          <span>{feature.text}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <p className="text-sm text-muted-foreground mb-4">
                      {plan.description}
                    </p>

                    <div className="pt-3 border-t border-border">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full text-lg" 
                        onClick={(e) => {
                          e.stopPropagation();
                          openPlanInfo(plan);
                        }}
                      >
                        <Info className="h-4 w-4 mr-2" />
                        Más información
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Continue and Share Buttons */}
          <div className="space-y-3">
            <Button 
              className="w-full min-h-touch text-lg px-6 sm:px-8 gap-0" 
              onClick={continuar}
              disabled={quoteLoading || !selectedKit}
            >
              <span className="">
                {quoteLoading ? 'Creando cotización...' : !selectedKit ? 'Selecciona un kit' : 'Continuar'}
              </span>
              {!quoteLoading && selectedKit && <ChevronRight style={{ marginLeft: '2.5px', width: '1.25rem', height: '1.25rem', strokeWidth: '3px'}}/>}
            </Button>

            {/* Show quote error if any */}
            {quoteError && (
              <p className="text-sm text-red-600 text-center">{quoteError}</p>
            )}
          </div>


          {/* Plan Information Modal */}
          <ProductInfoModal 
            isOpen={showProductModal} 
            onClose={closePlanModal} 
            product={selectedPlanForInfo} 
            type={selectedPlanForInfo?.type === 'retention' ? 'retention' : 'plan'}
            onKeepPlan={selectedPlanForInfo?.type === 'retention' ? keepPlan : undefined}
            onRemovePlan={selectedPlanForInfo?.type === 'retention' ? confirmRemovePlan : undefined}
          />
        </div>
      </div>
    </div>
  );
}